import styled from "@emotion/styled";
import { MNDE_MINT } from "@stepfinance/common";
import type { NFT } from "@stepfinance/nft";
import { lightGrey2, TokenIconText } from "@stepfinance/react";
import { Card } from "antd";
import type { FC } from "react";

import ItemAttributesBeakerIcon from "../../../assets/item-attributes-beaker.svg";
import ItemDescriptionSheetIcon from "../../../assets/item-description-sheet-icon.svg";
import ItemDetailsInfoIcon from "../../../assets/item-details-info-icon.svg";
import ItemPricingDollarIcon from "../../../assets/item-pricing-dollar-icon.svg";
import solanaFloorIcon from "../../../assets/nft/solanafloor-logo.svg";
import { useLanguageContext } from "../../../contexts/language/language";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import type { MetaplexAttribute } from "../../../models/metaplex";
import { isMarinadeNFT } from "../../../models/nfts/nfts";
import { formatSolNFTPrice } from "../../../utils/nft/number-utils";
import { basisPointsToDisplayPercentage } from "../../../utils/number-formatting/number-formatting";
import { ExplorerLinkDisplay } from "../../ExplorerLink/ExplorerLinkDisplay";
import RedirectLink from "../../RedirectLink/RedirectLink";
import { MonospacedText, TransparentWhiteText } from "../../shared";
import { StepComponentWrapperWithChevron } from "../../StepComponentWrapper";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";

interface NFTAttributesProps {
  nft: NFT;
}

export const NFTAttributes: FC<NFTAttributesProps> = ({ nft }) => {
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const {
    currentLanguageData: {
      common,
      pages: { nftGallery },
    },
  } = useLanguageContext();

  const mintAddress = nft.address;
  // Solarians utilizes "\n"
  const descriptionLines: string[] | undefined = nft.json?.description?.split("\n");
  const royaltyFeeDisplay = basisPointsToDisplayPercentage(nft.sellerFeeBasisPoints ?? 0);

  return (
    <NFTAttributesContainer>
      <Card bordered={false}>
        <StepComponentWrapperWithChevron
          genHeader={
            <SimpleFlexTableHeader icon={ItemPricingDollarIcon} headerText="Price" headerAltText="Price dollar icon" />
          }
          genBalance={null}
          isInitiallyCollapsed={nft.floorPrice !== 0}
        >
          <div className="super-large-price-display">
            <MonospacedText>{formatSolNFTPrice(nft.floorPrice)} SOL</MonospacedText>
          </div>
          <div className="price-text-container">
            <TransparentWhiteText>
              <MonospacedText>{calculatePriceForDisplayCurrency(nft.floorPriceUSD)}</MonospacedText>
            </TransparentWhiteText>
          </div>
          <div className="simple-flex-list">
            <div key={1}>
              <div>
                <strong>{common.balance}</strong>
              </div>
              <div>
                <MonospacedText>{nft.amount}</MonospacedText>
              </div>
            </div>
            <div key={2}>
              <div>
                <strong>{nftGallery.totalValue}</strong>
              </div>
              <div>
                <TransparentWhiteText>
                  {calculatePriceForDisplayCurrency(nft.floorPriceUSD !== null ? nft.amount * nft.floorPriceUSD : null)}
                </TransparentWhiteText>
              </div>
            </div>
          </div>
        </StepComponentWrapperWithChevron>
      </Card>
      {descriptionLines && descriptionLines.length && (
        <Card bordered={false}>
          <StepComponentWrapperWithChevron
            genHeader={
              <SimpleFlexTableHeader
                icon={ItemDescriptionSheetIcon}
                headerText={common.description}
                headerAltText="Description icon"
              />
            }
            genBalance={null}
          >
            {descriptionLines?.map((line: string, i: number) => (
              <GreyParagraph key={i}>{line}</GreyParagraph>
            ))}
          </StepComponentWrapperWithChevron>
        </Card>
      )}
      {nft.json?.attributes?.length && nft.json?.attributes?.length > 0 && (
        <Card bordered={false}>
          <StepComponentWrapperWithChevron
            genHeader={
              <SimpleFlexTableHeader
                icon={ItemAttributesBeakerIcon}
                headerText={common.attributes}
                headerAltText="Attributes icon"
              />
            }
            genBalance={null}
          >
            <div className="simple-flex-list">
              {nft.json.attributes.map((attribute: MetaplexAttribute, i: number) => {
                return (
                  <div key={i}>
                    <div>
                      <strong>{attribute.trait_type}</strong>
                    </div>
                    <div>{attribute.value}</div>
                  </div>
                );
              })}
              {isMarinadeNFT(nft) && (
                <div>
                  <div>
                    <strong>Underlying Assets</strong>
                  </div>
                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                    <CellValue cellType="number" hideable={false}>
                      {nft.json.properties?.mnde_amount as number}
                    </CellValue>
                    <TokenIcon mintAddress={MNDE_MINT} style={{ marginLeft: "10px" }} />
                    <TokenIconText>{"MNDE"}</TokenIconText>
                  </div>
                </div>
              )}
            </div>
          </StepComponentWrapperWithChevron>
        </Card>
      )}
      <Card bordered={false}>
        <StepComponentWrapperWithChevron
          genHeader={
            <SimpleFlexTableHeader
              icon={ItemDetailsInfoIcon}
              headerText={common.details}
              headerAltText="Details icon"
            />
          }
          genBalance={null}
        >
          <div className="simple-flex-list">
            <div>
              <div>
                <strong>{nftGallery.mintAddress}</strong>
              </div>
              {mintAddress ? (
                <ExplorerLinkDisplay type="address" address={mintAddress} addressLength={4} />
              ) : (
                <div>?</div>
              )}
            </div>
            <div>
              <div>
                <strong>{nftGallery.owner}</strong>
              </div>
              <ExplorerLinkDisplay type="address" address={nft.owner} addressLength={4} />
            </div>
            <div>
              <div>
                <strong>{common.platform}</strong>
              </div>
              <div>&quot;Metaplex&quot;</div>
            </div>
            <div>
              <div>
                <strong>{nftGallery.creatorFee}</strong>
              </div>
              <div>
                <MonospacedText>{royaltyFeeDisplay}</MonospacedText>
              </div>
            </div>
            {nft.solanaFloorData?.collection?.slug && (
              <div>
                <div>
                  <strong>{nftGallery.collectionStats}</strong>
                </div>
                <div>
                  <RedirectLink href={`https://solanafloor.com/nft/${nft.solanaFloorData.collection.slug}`}>
                    <img alt="SF" className={"url-icon-sf"} src={solanaFloorIcon} />
                    <span>{nftGallery.viewOnSF}</span>
                  </RedirectLink>
                </div>
              </div>
            )}
          </div>
        </StepComponentWrapperWithChevron>
      </Card>
    </NFTAttributesContainer>
  );
};

const NFTAttributesContainer = styled.div`
  .ant-card {
    margin-bottom: 20px;
    .ant-card-body {
      padding: 14px 20px;
      .ant-collapse-content-box {
        padding-bottom: 0 !important;
        p {
          margin-bottom: 0;
        }
      }
    }
  }

  .simple-flex-list {
    display: flex;
    flex-direction: column;

    > div {
      display: flex;
      justify-content: space-between;

      > div:first-of-type {
        width: 40%;
        text-transform: capitalize;
      }

      > div:nth-of-type(2) {
        text-align: right;
        width: 60%;
      }

      .property-name {
        font-weight: 800;
      }

      .url-icon-sf {
        margin-right: 6px;
        border-radius: 6px;
        width: 20px;
        height: 20px;
      }
    }

    > div + div {
      margin-top: 10px;
    }
  }

  .nft-name {
    font-weight: 800 !important;
  }

  .super-large-price-display {
    span {
      width: 100%;
      font-size: 60px;
      line-height: 66px;
      font-weight: 800;

      // 576px = Breakpoints.S i.e. mobile
      @media (max-width: 576px) {
        font-size: 40px;
      }
    }
  }

  .price-text-container {
    width: 100%;
    margin-bottom: 20px;
  }
`;

const SimpleFlexTableHeader: React.FC<{
  icon: string;
  headerText: string;
  headerAltText: string;
}> = ({ icon, headerText, headerAltText }) => (
  <SimpleFlexTable>
    <img src={icon} alt={headerAltText} />
    <span>{headerText}</span>
  </SimpleFlexTable>
);

const GreyParagraph = styled.p`
  color: ${lightGrey2};
`;

const SimpleFlexTable = styled.div`
  display: flex;
  img {
    width: 20px;
    height: 20px;
    margin: auto 0;
  }
  span {
    font-size: 14px;
    font-weight: 800;
    margin-left: 10px;
  }
`;
